import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import { Route, Routes, useLocation } from "react-router-dom";
import {
  GamepadRounded,
  HistoryRounded,
  LogoutRounded,
} from "@mui/icons-material";
import { COLOR_MAIN, COLOR_WHITE } from "../../../data/colors/Colors";
import { Stack } from "@mui/material";
import SharedPreferences from "../../../core/utils/SharedPreferences";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { DrawerItem, DrawerItemView } from "./DrawerItemView";
import { Error404View } from "../../component/404View";
import { CustomTextView } from "../../component/CustomTextView";
import { useTranslation } from "react-i18next";
import { STORAGE_KEYS } from "../../../data/constant/Constants";
import { UserAvatarView } from "./avatar/UserAvatarView";
import { LanguageSelectView } from "./LanguageSelectView";
import { PlayGameFabView } from "./PlayGameFabView";
import { Player } from "../../../domain/model/User";
import { PlayView } from "../play/PlayView";
import { UnfnishedGamesView } from "../unfinishedGames/UnfinishedGamesView";
import { PortraitOnlyContainer } from "../../component/PortraitOnlyContainer";

const drawerWidth = 280;

export function HomeView() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [player, setPlayer] = React.useState<Player>();

  const drawerRouterItems: Array<DrawerItem | undefined> = [
    {
      isRoute: true,
      title: t("play"),
      icon: <GamepadRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_PLAY,
      href: ROUTE_ENDPOINTS.HOME_PLAY,
      onCloseDrawer: () => setMobileOpen(false),
    },
    {
      isRoute: true,
      title: t("unfinished_games"),
      icon: <HistoryRounded />,
      isSelected: location.pathname === ROUTE_ENDPOINTS.HOME_UNFINISHED_GAMES,
      href: ROUTE_ENDPOINTS.HOME_UNFINISHED_GAMES,
      onCloseDrawer: () => setMobileOpen(false),
    },
  ];

  const drawerButtonsItems: Array<DrawerItem> = [
    {
      isRoute: false,
      title: t("logout"),
      icon: <LogoutRounded />,
      onClick: () => {
        SharedPreferences.clearByKey(STORAGE_KEYS.TOKEN);
      },
      textColor: COLOR_WHITE,
      bgColor: "transparent",
    },
  ];

  const drawer = (
    <Box
      sx={{
        minHeight: "100%",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      <Stack
        spacing={0}
        paddingBottom={2}
        minHeight={"100%"}
        justifyContent={"space-between"}
        sx={{ background: COLOR_MAIN }}
      >
        <Stack width={"100%"}>
          <Toolbar />
          <Box height={20} />
          <UserAvatarView onFetchPlayer={(p) => setPlayer(p)} />
          <Box height={10} />
          {drawerRouterItems.map((item) => (
            <Stack key={item?.href}>
              {item && (
                <DrawerItemView
                  icon={item.icon}
                  isRoute={item.isRoute}
                  key={item.title}
                  title={item.title}
                  isSelected={item.isSelected}
                  href={item.href}
                  onCloseDrawer={item.onCloseDrawer}
                />
              )}
            </Stack>
          ))}
        </Stack>
        <Stack>
          {drawerButtonsItems.map((item) => (
            <DrawerItemView
              key={item.title}
              icon={item.icon}
              isRoute={item.isRoute}
              onClick={item.onClick}
              title={item.title}
              textColor={item.textColor}
              bgColor={item.bgColor}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );

  const isRtl = i18n.dir() == "rtl";

  return (
    <PortraitOnlyContainer>
      <Box sx={{ display: "flex" }}>
        <PlayGameFabView player={player} />
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: COLOR_MAIN,
            borderBottomRightRadius: 14,
            borderBottomLeftRadius: 14,
          }}
        >
          <Toolbar>
            <Stack
              width="100%"
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Stack direction={"row"} alignItems={"center"}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    ml: isRtl ? 2 : 0,
                    mr: isRtl ? 0 : 2,
                    display: { sm: "none" },
                  }}
                >
                  <MenuIcon />
                </IconButton>
                <CustomTextView
                  text={t("app_name")}
                  variant="h5"
                  fontWeight="bold"
                  textColor={COLOR_WHITE}
                />
              </Stack>
              <LanguageSelectView />
            </Stack>
          </Toolbar>
        </AppBar>
        <Box sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            anchor={isRtl ? "right" : "left"}
            onTransitionEnd={handleDrawerTransitionEnd}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                borderWidth: 0,
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            anchor={isRtl ? "right" : "left"}
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
                borderWidth: 0,
                borderBottomRightRadius: isRtl ? 0 : 30,
                borderBottomLeftRadius: isRtl ? 30 : 0,
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          <Toolbar />
          <Routes>
            <Route
              path={ROUTE_ENDPOINTS.PLAY}
              element={<PlayView player={player} />}
            />
            <Route
              path={ROUTE_ENDPOINTS.UNFINISHED_GAMES}
              element={<UnfnishedGamesView player={player} />}
            />
            <Route path="*" element={<Error404View />} />
          </Routes>
        </Box>
      </Box>
    </PortraitOnlyContainer>
  );
}
